import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../contexts/AuthContext';

// Relatív API útvonalakat használunk, így a szerveren is működni fog
const API_BASE_URL = '';

// Munkaidő számítás függvény
function calculateWorkTime(trips) {
  const timeToMinutes = (timeStr) => {
    if (!timeStr || timeStr === '-') return null
    const parts = timeStr.split(':')
    if (parts.length !== 2) return null
    const hours = parseInt(parts[0], 10)
    const minutes = parseInt(parts[1], 10)
    if (isNaN(hours) || isNaN(minutes)) return null
    return hours * 60 + minutes
  }
  
  const timeToMinutesForSort = (timeStr) => {
    if (!timeStr || timeStr === '-') return 9999
    const parts = timeStr.split(':')
    if (parts.length !== 2) return 9999
    const hours = parseInt(parts[0], 10)
    const minutes = parseInt(parts[1], 10)
    if (isNaN(hours) || isNaN(minutes)) return 9999
    if (hours === 0) return 24 * 60 + minutes
    if (hours === 1) return 25 * 60 + minutes
    return hours * 60 + minutes
  }
  
  const minutesToTime = (mins) => {
    if (mins === null || mins === undefined || isNaN(mins)) return '--:--'
    const h = Math.floor(Math.abs(mins) / 60)
    const m = Math.abs(mins) % 60
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
  }
  
  const getTripDuration = (trip) => {
    const start = timeToMinutes(trip.startTime)
    const end = timeToMinutes(trip.endTime)
    if (start === null || end === null) return 0
    let duration = end - start
    if (duration < 0) duration += 24 * 60
    return duration
  }
  
  const isSignIn = (t) => t.tripNumber?.toLowerCase().includes('jelentkezés')
  const isSignOut = (t) => t.tripNumber?.toLowerCase().includes('végzés')
  const isBreak = (t) => t.tripNumber?.toLowerCase().includes('szünet') || t.tripNumber?.toLowerCase().includes('munkaközi')
  const isMaintenance = (t) => {
    const lower = t.tripNumber?.toLowerCase() || ''
    return lower.includes('karbantartás') || lower.includes('karbantartási')
  }
  const isRevenue = (t) => {
    const lower = t.tripNumber?.toLowerCase() || ''
    return lower.includes('bevétel') || lower.includes('elszámolás') || lower.includes('elszámolási')
  }
  const isBusRoute = (t) => !!t.nyomvon || !!t.lineNumber
  const isBusHandover = (t) => {
    const lower = t.tripNumber?.toLowerCase() || ''
    return lower.includes('járműátadás') || lower.includes('járműátadási') || 
           lower.includes('jármű átadás') || lower.includes('jármű átadási')
  }
  const isBusTakeover = (t) => {
    const lower = t.tripNumber?.toLowerCase() || ''
    return lower.includes('járműátvétel') || lower.includes('járműátvételi') ||
           lower.includes('jármű átvétel') || lower.includes('jármű átvételi')
  }
  const isTecido = (t) => {
    const lower = t.tripNumber?.toLowerCase() || ''
    return lower.includes('tecido') || lower.includes('tec ido')
  }
  const hasKm = (t) => t.km && parseFloat(t.km) > 0
  
  const signInTrip = trips.find(isSignIn)
  const signOutTrip = trips.find(isSignOut)
  
  const signInTime = timeToMinutes(signInTrip?.startTime)
  const signOutTime = timeToMinutes(signOutTrip?.startTime)
  
  let szolgalatiIdo = null
  if (signInTime !== null && signOutTime !== null) {
    szolgalatiIdo = signOutTime - signInTime
    if (szolgalatiIdo < 0) szolgalatiIdo += 24 * 60
  }
  
  let szunetIdo = 0
  let karbantartasIdo = 0
  let bevetelIdo = 0
  let kiallasiIdoRendelkezesre = 0
  let vezetesiIdo = 0
  let jarmuAtadasIdo = 0
  let jarmuAtvetelIdo = 0
  let napiFix = 0
  
  const sortedTrips = [...trips].sort((a, b) => {
    const timeA = timeToMinutesForSort(a.startTime)
    const timeB = timeToMinutesForSort(b.startTime)
    return timeA - timeB
  })
  
  for (let i = 0; i < sortedTrips.length; i++) {
    const trip = sortedTrips[i]
    const duration = getTripDuration(trip)
    
    if (isSignIn(trip) || isSignOut(trip)) {
      continue
    } else if (isBreak(trip)) {
      szunetIdo += duration
    } else if (isMaintenance(trip)) {
      karbantartasIdo += duration
    } else if (isRevenue(trip)) {
      bevetelIdo += duration
    } else if (isBusHandover(trip)) {
      jarmuAtadasIdo += duration
    } else if (isBusTakeover(trip)) {
      jarmuAtvetelIdo += duration
    }
    
    if (hasKm(trip)) {
      vezetesiIdo += duration
    }
    
    // Kiállási idő számítása: jelenlegi járat end_time és következő NEM-TECIDO járat start_time közötti idő
    // TECIDO járatokat kihagyjuk, mert azok várakozási idők, nem valódi járatok
    if (!isTecido(trip) && i < sortedTrips.length - 1) {
      // Következő nem-TECIDO járat keresése
      let nextTrip = null
      for (let j = i + 1; j < sortedTrips.length; j++) {
        if (!isTecido(sortedTrips[j])) {
          nextTrip = sortedTrips[j]
          break
        }
      }
      
      if (nextTrip) {
        const currentEndTime = timeToMinutes(trip.endTime)
        const nextStartTime = timeToMinutes(nextTrip.startTime)
        
        if (currentEndTime !== null && nextStartTime !== null) {
          let kiallasiIdo = nextStartTime - currentEndTime
          if (kiallasiIdo < 0) kiallasiIdo += 24 * 60
          
          if (kiallasiIdo > 20) {
            const rendelkezesre = kiallasiIdo - 20
            kiallasiIdoRendelkezesre += rendelkezesre
          }
        }
      }
    }
  }
  
  let munkaiDo = null
  if (szolgalatiIdo !== null) {
    munkaiDo = szolgalatiIdo - szunetIdo - kiallasiIdoRendelkezesre
  }
  
  napiFix = jarmuAtadasIdo + jarmuAtvetelIdo + bevetelIdo + karbantartasIdo
  
  return {
    szolgalatiIdo: minutesToTime(szolgalatiIdo),
    munkaiDo: minutesToTime(munkaiDo),
    rendelkezesreAllasIdo: minutesToTime(kiallasiIdoRendelkezesre),
    vezetesiIdo: minutesToTime(vezetesiIdo),
    szunetIdo: minutesToTime(szunetIdo),
    napiFix: minutesToTime(napiFix),
    raw: { szolgalatiIdo, munkaiDo, kiallasiIdoRendelkezesre, vezetesiIdo, napiFix }
  }
}

function Trips() {
  const { getAuthHeaders, isAdmin, hasPermission } = useAuth();
  const canUploadTrips = hasPermission('canUploadTrips');
  // Alapértelmezett dátum: ma
  const today = new Date();
  const defaultDate = today.toISOString().split('T')[0];
  
  const [selectedDate, setSelectedDate] = useState(defaultDate);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [daysWithTrips, setDaysWithTrips] = useState([]);
  
  // Ford szűrő state-ek
  const [selectedFords, setSelectedFords] = useState(null); // null = összes kijelölve
  const [showFordsDropdown, setShowFordsDropdown] = useState(false);
  const fordsDropdownRef = useRef(null);

  // PDF export szűrő state-ek
  const [showMaintenanceInExport, setShowMaintenanceInExport] = useState(() => {
    const saved = localStorage.getItem('trips_showMaintenanceInExport');
    return saved ? JSON.parse(saved) : true;
  });
  const [showRevenueInExport, setShowRevenueInExport] = useState(() => {
    const saved = localStorage.getItem('trips_showRevenueInExport');
    return saved ? JSON.parse(saved) : true;
  });
  const [showTecInExport, setShowTecInExport] = useState(() => {
    const saved = localStorage.getItem('trips_showTecInExport');
    return saved ? JSON.parse(saved) : true;
  });
  const [showHeaderInExport, setShowHeaderInExport] = useState(() => {
    const saved = localStorage.getItem('trips_showHeaderInExport');
    return saved ? JSON.parse(saved) : true;
  });

  // Export beállítások mentése
  useEffect(() => {
    localStorage.setItem('trips_showMaintenanceInExport', JSON.stringify(showMaintenanceInExport));
  }, [showMaintenanceInExport]);

  useEffect(() => {
    localStorage.setItem('trips_showRevenueInExport', JSON.stringify(showRevenueInExport));
  }, [showRevenueInExport]);

  useEffect(() => {
    localStorage.setItem('trips_showTecInExport', JSON.stringify(showTecInExport));
  }, [showTecInExport]);

  useEffect(() => {
    localStorage.setItem('trips_showHeaderInExport', JSON.stringify(showHeaderInExport));
  }, [showHeaderInExport]);

  // Járatok lekérdezése
  const fetchTrips = async (date) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/schedule-calendar/trips/${date}`);
      setTrips(response.data || []);
    } catch (err) {
      console.error('Hiba a járatok lekérdezése során:', err);
      if (err.response?.status === 404) {
        setTrips([]);
        setError('Nincs járat erre a napra');
      } else {
        setError('Hiba a járatok lekérdezése során: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  // Járatok betöltése (XML-ből generálás)
  const loadTrips = async () => {
    setLoadingTrips(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/schedule-calendar/load-trips/${selectedDate}`, {}, {
        headers: getAuthHeaders()
      });
      setSuccess(response.data.message || `${response.data.count} járat sikeresen betöltve`);
      // Újra lekérdezzük a járatokat
      await fetchTrips(selectedDate);
      // Frissítjük a járatos napok listáját
      await fetchDaysWithTrips();
    } catch (err) {
      console.error('Hiba a járatok betöltése során:', err);
      setError('Hiba a járatok betöltése során: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingTrips(false);
    }
  };

  // Járatos napok lekérdezése
  const fetchDaysWithTrips = async () => {
    try {
      const month = selectedDate.substring(0, 7); // YYYY-MM
      const response = await axios.get(`${API_BASE_URL}/api/schedule-calendar/days-with-trips?month=${month}`);
      setDaysWithTrips(response.data || []);
    } catch (err) {
      console.error('Hiba a járatos napok lekérdezése során:', err);
    }
  };

  // Dátum változásakor lekérdezzük a járatokat és töröljük az üzeneteket
  useEffect(() => {
    if (selectedDate) {
      setError(null);
      setSuccess(null);
      fetchTrips(selectedDate);
      fetchDaysWithTrips();
    }
  }, [selectedDate]);

  // Sikeres üzenet automatikus eltüntetése 10 másodperc után
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Járatok csoportosítása járművenként
  const groupedTrips = trips.reduce((acc, trip) => {
    if (!acc[trip.ford]) {
      acc[trip.ford] = [];
    }
    acc[trip.ford].push(trip);
    return acc;
  }, {});

  // Ford szűrő függvények
  const allFordNames = Object.keys(groupedTrips).sort();
  const isAllFordsSelected = selectedFords === null || (selectedFords.length === allFordNames.length);
  
  const toggleFordSelection = (fordName) => {
    if (selectedFords === null) {
      setSelectedFords(allFordNames.filter(f => f !== fordName));
    } else if (selectedFords.includes(fordName)) {
      const newSelection = selectedFords.filter(f => f !== fordName);
      setSelectedFords(newSelection.length === 0 ? null : newSelection);
    } else {
      const newSelection = [...selectedFords, fordName];
      setSelectedFords(newSelection.length === allFordNames.length ? null : newSelection);
    }
  };
  
  const toggleAllFords = () => {
    if (isAllFordsSelected) {
      setSelectedFords([]);
    } else {
      setSelectedFords(null);
    }
  };
  
  const isFordSelected = (fordName) => {
    return selectedFords === null || selectedFords.includes(fordName);
  };

  // Dropdown bezárása kattintásra a dokumentumon
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fordsDropdownRef.current && !fordsDropdownRef.current.contains(event.target)) {
        setShowFordsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Adatbázis tisztítás state-ek
  const [showCleanup, setShowCleanup] = useState(false);
  const [cleanupDate, setCleanupDate] = useState('');
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupStats, setCleanupStats] = useState(null);
  const [cleanupError, setCleanupError] = useState(null);
  const [cleanupSuccess, setCleanupSuccess] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Járat statisztika lekérdezése (csak admin)
  const fetchTripsStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/schedule-calendar/trips-stats`, {
        headers: getAuthHeaders()
      });
      setCleanupStats(response.data);
    } catch (err) {
      console.error('Hiba a statisztika lekérdezése során:', err);
    }
  };

  useEffect(() => {
    if (showCleanup && isAdmin()) {
      fetchTripsStats();
    }
  }, [showCleanup]);

  // Tisztítás előzetes ellenőrzés
  const handleCleanupCheck = async () => {
    if (!cleanupDate) {
      setCleanupError('Kérlek adj meg egy dátumot!');
      return;
    }
    setCleanupError(null);

    // Ellenőrizzük a 60 napos korlátot frontend oldalon is
    const cutoff = new Date(cleanupDate);
    const minDate = new Date();
    minDate.setDate(minDate.getDate() - 60);
    if (cutoff > minDate) {
      setCleanupError(`Biztonsági okokból csak 60 napnál régebbi járatokat lehet törölni. Legkorábbi megengedett dátum: ${minDate.toISOString().split('T')[0]}`);
      return;
    }

    // Megerősítő dialógus megnyitása
    setConfirmDialogOpen(true);
  };

  // Tisztítás végrehajtása
  const executeCleanup = async () => {
    setConfirmDialogOpen(false);
    setCleanupLoading(true);
    setCleanupError(null);
    setCleanupSuccess(null);
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/schedule-calendar/cleanup-trips?before=${cleanupDate}`,
        { headers: getAuthHeaders() }
      );
      setCleanupSuccess(response.data.message);
      fetchTripsStats();
      // Frissítjük a járatokat is
      if (selectedDate) {
        fetchTrips(selectedDate);
        fetchDaysWithTrips();
      }
    } catch (err) {
      setCleanupError(err.response?.data?.message || 'Hiba történt a törlés során');
    } finally {
      setCleanupLoading(false);
    }
  };

  // Excel exportálás
  const exportToExcel = () => {
    if (trips.length === 0) return;
    
    // Adatok előkészítése az Excel exporthoz
    const excelData = trips
      .sort((a, b) => {
        // Először fordaszám szerint, majd sorszám szerint rendezés
        if (a.ford !== b.ford) return a.ford.localeCompare(b.ford);
        return a.sortOrder - b.sortOrder;
      })
      .map(trip => ({
        'Fordaszám': trip.ford || '',
        'Vonalszám': trip.lineNumber || '',
        'Járatszám': trip.tripNumber || '',
        'Indulási idő': trip.startTime || '',
        'Indulási hely': trip.startLocation || '',
        'Érkezési idő': trip.endTime || '',
        'Érkezési hely': trip.endLocation || '',
        'Km': trip.km?.toFixed(2) || '0.00'
      }));
    
    // Worksheet létrehozása
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // Oszlopszélességek beállítása
    ws['!cols'] = [
      { wch: 12 },  // Fordaszám
      { wch: 10 },  // Vonalszám
      { wch: 25 },  // Járatszám
      { wch: 12 },  // Indulási idő
      { wch: 25 },  // Indulási hely
      { wch: 12 },  // Érkezési idő
      { wch: 25 },  // Érkezési hely
      { wch: 10 }   // Km
    ];
    
    // Workbook létrehozása
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Járatok');
    
    // Fájlnév generálása
    const fileName = `jaratok_${selectedDate}.xlsx`;
    
    // Letöltés
    XLSX.writeFile(wb, fileName);
  };

  // PDF exportálás
  const exportToPDF = () => {
    try {
      if (trips.length === 0) return;

      // Járatok csoportosítása ford szerint
      const regularFordak = Object.entries(groupedTrips)
        .filter(([ford]) => isFordSelected(ford))
        .map(([ford, fordTrips]) => ({
          ford,
          trips: fordTrips
        }));

      if (regularFordak.length === 0) {
        alert('Nincs exportálható forda!');
        return;
      }

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();

      regularFordak.forEach((fordData, fordIndex) => {
        if (fordIndex > 0) {
          doc.addPage();
        }

        // Járatok szűrése a kapcsolók alapján
        let filteredTrips = fordData.trips;
        if (!showMaintenanceInExport || !showRevenueInExport || !showTecInExport) {
          filteredTrips = fordData.trips.filter(trip => {
            const isMaintenance = trip.tripNumber?.toLowerCase().includes('karbantartás') || 
                                 trip.tripNumber?.toLowerCase().includes('karbantartási');
            const isRevenue = trip.tripNumber?.toLowerCase().includes('bevétel') || 
                             trip.tripNumber?.toLowerCase().includes('elszámolás') ||
                             trip.tripNumber?.toLowerCase().includes('elszámolási');
            const isTec = trip.tripNumber?.toLowerCase().includes('tecido');
            
            if (isMaintenance && !showMaintenanceInExport) return false;
            if (isRevenue && !showRevenueInExport) return false;
            if (isTec && !showTecInExport) return false;
            return true;
          });
        }

        let yPos = 10;
        const leftX = 14;
        const centerX = pageWidth / 2;
        const rightX = pageWidth - 14;

        const fixHungarianChars = (text) => {
          if (!text) return '';
          return text.replace(/á/g, 'a').replace(/Á/g, 'A')
                     .replace(/é/g, 'e').replace(/É/g, 'E')
                     .replace(/í/g, 'i').replace(/Í/g, 'I')
                     .replace(/ó/g, 'o').replace(/Ó/g, 'O')
                     .replace(/ö/g, 'o').replace(/Ö/g, 'O')
                     .replace(/ő/g, 'o').replace(/Ő/g, 'O')
                     .replace(/ú/g, 'u').replace(/Ú/g, 'U')
                     .replace(/ü/g, 'u').replace(/Ü/g, 'U')
                     .replace(/ű/g, 'u').replace(/Ű/g, 'U');
        };

        // ── 1. SOR: Cég + Oldal ──────────────────────────────
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const now = new Date();
        const downloadDate = now.toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/ /g, '');
        const downloadHours = String(now.getHours()).padStart(2, '0');
        const downloadMins = String(now.getMinutes()).padStart(2, '0');
        const downloadTime = `${downloadDate} ${downloadHours}:${downloadMins}.`;
        doc.text('T-Busz Tatabanyai Kozlekedesi Kft.', leftX, yPos);
        doc.text(`Lehivas ideje:   ${downloadTime}`, rightX, yPos, { align: 'right' });
        yPos += 8;

        // ── 2. INFO SZEKCIÓ (3 oszlop, ha fejléc engedélyezve) ──
        const dateForPdf = selectedDate.split('-').join('.') + '.';

        if (showHeaderInExport) {
          const sY = yPos;
          // BAL: km mezők
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          doc.text('Zaro km  :  _______________', leftX, sY);
          doc.text('Kezdo km :  _______________', leftX, sY + 6);
          doc.text('Osszesen km: _______________', leftX, sY + 12);
          // KÖZÉP: cím + dátum
          doc.setFontSize(13);
          doc.setFont('helvetica', 'bold');
          doc.text('Gkv napi tevekenyseg', centerX, sY + 4, { align: 'center' });
          doc.setFontSize(11);
          doc.setFont('helvetica', 'normal');
          doc.text(`${dateForPdf} napra`, centerX, sY + 11, { align: 'center' });
          // JOBB: fellép/lelép
          doc.setFontSize(9);
          doc.text('Fellep teny  :  _____:_____', rightX, sY, { align: 'right' });
          doc.text('Lelep teny   :  _____:_____', rightX, sY + 6, { align: 'right' });
          yPos = sY + 22;
        } else {
          doc.setFontSize(13);
          doc.setFont('helvetica', 'bold');
          doc.text('Gkv napi tevekenyseg', centerX, yPos, { align: 'center' });
          yPos += 10;
        }

        // ── 3. VEZETŐ / FORDA SOR ────────────────────────────
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('(..........) .............................', leftX, yPos);
        doc.text(`Tev.lap szama: ${fordData.ford}`, centerX, yPos, { align: 'center' });
        doc.text('Frsz: .............', rightX, yPos, { align: 'right' });
        doc.setFont('helvetica', 'normal');
        yPos += 8;

        // ── 4. FŐTÁBLÁZAT ────────────────────────────────────
        const isServiceRow = (trip) => trip && /^\d+$/.test((trip.tripNumber || '').trim());
        const totalKm = filteredTrips.reduce((sum, trip) => sum + (parseFloat(trip.km) || 0), 0);
        const tableData = filteredTrips.map(trip => [
          fordData.ford,
          trip.lineNumber || '',
          fixHungarianChars(trip.tripNumber || ''),
          fixHungarianChars(trip.startLocation || ''),
          trip.startTime || '',
          fixHungarianChars(trip.endLocation || ''),
          trip.endTime || '',
          trip.km > 0 ? Number(trip.km).toFixed(2).replace('.', ',') : ''
        ]);

        autoTable(doc, {
          head: [['Forda', 'Vonal', 'Jarat', 'Honnan', 'Ind.', 'Hova', 'Erk.', 'Km']],
          body: tableData,
          startY: yPos,
          styles: {
            fontSize: 8,
            cellPadding: 2,
            lineColor: [180, 180, 180],
            lineWidth: 0.1
          },
          headStyles: {
            fillColor: [180, 180, 180],
            textColor: [0, 0, 0],
            fontStyle: 'bold'
          },
          columnStyles: {
            0: { cellWidth: 18 },
            1: { cellWidth: 12 },
            2: { cellWidth: 20 },
            3: { cellWidth: 38 },
            4: { cellWidth: 14 },
            5: { cellWidth: 38 },
            6: { cellWidth: 14 },
            7: { cellWidth: 'auto', halign: 'right' }
          },
          didParseCell: (data) => {
            if (data.section === 'body') {
              const trip = filteredTrips[data.row.index];
              if (isServiceRow(trip) && (data.column.index === 3 || data.column.index === 5)) {
                data.cell.styles.fontStyle = 'bold';
              }
            }
          }
        });

        // ── 5. NOTE BEKEZDÉS ─────────────────────────────────
        const allNotes = fordData.trips
          .filter(t => t.note && t.note.trim())
          .map(t => fixHungarianChars(t.note.trim()))
          .filter((v, i, a) => a.indexOf(v) === i)
          .join(' ');
        let finalY = doc.lastAutoTable.finalY || yPos + 20;
        if (allNotes) {
          finalY += 4;
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'normal');
          const noteLines = doc.splitTextToSize(allNotes, pageWidth - leftX * 2);
          doc.text(noteLines, leftX, finalY);
          finalY += noteLines.length * 3.5 + 1;
        }
        yPos = finalY + 2;

        // ── 6. ÖSSZESÍTŐ TÁBLÁZAT ────────────────────────────
        const workTime = calculateWorkTime(fordData.trips);
        const signIn = filteredTrips.find(t => t.tripNumber?.toLowerCase().includes('jelentkezés'));
        const signOut = filteredTrips.find(t => t.tripNumber?.toLowerCase().includes('végzés'));

        const summaryData = [[
          signIn?.startTime || '-',
          signOut?.endTime || '-',
          workTime.szolgalatiIdo,
          workTime.vezetesiIdo || '-',
          workTime.napiFix || '-',
          workTime.munkaiDo,
          workTime.rendelkezesreAllasIdo,
          totalKm.toFixed(2).replace('.', ',')
        ]];

        autoTable(doc, {
          head: [['Szolg.ba', 'Szolg.bol', 'Szolg.ido', 'Vez.ido', 'Napi fix', 'Munkaido', 'Rendelk.ido', 'Km']],
          body: summaryData,
          startY: yPos,
          styles: {
            fontSize: 8,
            cellPadding: 2,
            lineColor: [180, 180, 180],
            lineWidth: 0.1
          },
          headStyles: {
            fillColor: [180, 180, 180],
            textColor: [0, 0, 0],
            fontStyle: 'bold'
          },
          columnStyles: {
            7: { halign: 'right' }
          }
        });
      });

      const fileName = `fordak_${selectedDate}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('PDF generálási hiba:', error);
      alert(`Hiba történt a PDF generálása közben: ${error.message}`);
    }
  };

  // 60 nappal ezelőtti dátum (max megengedett törlési dátum)
  const maxCleanupDate = new Date();
  maxCleanupDate.setDate(maxCleanupDate.getDate() - 60);
  const maxCleanupDateStr = maxCleanupDate.toISOString().split('T')[0];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Járatok
      </Typography>

      {/* Dátum választó és Járatok betöltése gomb */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Dátum"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ minWidth: 200 }}
          />
            
            {canUploadTrips && (
              <Button
                variant="contained"
                color="primary"
                onClick={loadTrips}
                disabled={loadingTrips || !selectedDate}
                startIcon={loadingTrips ? <CircularProgress size={20} /> : <RefreshIcon />}
              >
                {loadingTrips ? 'Betöltés...' : 'Járatok betöltése'}
              </Button>
            )}

            {trips.length > 0 && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  onClick={exportToExcel}
                  startIcon={<DownloadIcon />}
                >
                  Excel letöltés
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={exportToPDF}
                  startIcon={<PictureAsPdfIcon />}
                >
                  PDF letöltés
                </Button>
              </>
            )}

            {/* Ford szűrő dropdown */}
            {allFordNames.length > 0 && (
              <Box ref={fordsDropdownRef} sx={{ position: 'relative' }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowFordsDropdown(!showFordsDropdown)}
                  sx={{ minWidth: 120 }}
                >
                  🎯 Fordák {!isAllFordsSelected && selectedFords ? `(${selectedFords.length})` : ''}
                </Button>
                {showFordsDropdown && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      zIndex: 1000,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      boxShadow: 3,
                      minWidth: 200,
                      maxHeight: 300,
                      overflowY: 'auto',
                      mt: 0.5
                    }}
                  >
                    <Box
                      component="label"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        cursor: 'pointer',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        fontWeight: 'bold',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isAllFordsSelected}
                        onChange={toggleAllFords}
                        style={{ marginRight: '8px' }}
                      />
                      (Az összes kijelölése)
                    </Box>
                    {allFordNames.map(fordName => (
                      <Box
                        component="label"
                        key={fordName}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 1,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isFordSelected(fordName)}
                          onChange={() => toggleFordSelection(fordName)}
                          style={{ marginRight: '8px' }}
                        />
                        {fordName}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {trips.length} járat
            </Typography>
          </Box>

          {/* PDF export szűrők */}
          {trips.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                PDF export beállítások:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={showRevenueInExport} onChange={(e) => setShowRevenueInExport(e.target.checked)} />}
                  label={<Typography variant="body2">Bevétel elszámolás idő feltüntetése</Typography>}
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={showMaintenanceInExport} onChange={(e) => setShowMaintenanceInExport(e.target.checked)} />}
                  label={<Typography variant="body2">Karbantartási idő feltüntetése</Typography>}
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={showTecInExport} onChange={(e) => setShowTecInExport(e.target.checked)} />}
                  label={<Typography variant="body2">Tecidők feltüntetése</Typography>}
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={showHeaderInExport} onChange={(e) => setShowHeaderInExport(e.target.checked)} />}
                  label={<Typography variant="body2">Fejléc feltüntetése</Typography>}
                />
              </Box>
            </Box>
          )}

          {/* Járatos napok jelzése */}
          {daysWithTrips.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="body2" color="text.primary">
                <strong>Járatokkal rendelkező napok ebben a hónapban:</strong>
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {daysWithTrips.map(day => (
                  <Chip
                    key={day}
                    label={day}
                    size="small"
                    color={day === selectedDate ? 'primary' : 'default'}
                    onClick={() => setSelectedDate(day)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}
        </Paper>

        {/* Járatok megjelenítése */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : trips.length === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography variant="body1" color="text.secondary" align="center">
              Nincs járat erre a napra. Kattints a "Járatok betöltése" gombra a járatok generálásához.
            </Typography>
          </Paper>
        ) : (
          <Box>
            {Object.entries(groupedTrips)
              .filter(([ford]) => isFordSelected(ford))
              .map(([ford, fordTrips]) => (
              <Paper key={ford} sx={{ mb: 2, p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {ford} <Chip label={`${fordTrips.length} járat`} size="small" sx={{ ml: 1 }} />
                </Typography>
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Sorszám</TableCell>
                        <TableCell>Vonal</TableCell>
                        <TableCell>Járatszám</TableCell>
                        <TableCell>Indulás</TableCell>
                        <TableCell>Indulási hely</TableCell>
                        <TableCell>Érkezés</TableCell>
                        <TableCell>Érkezési hely</TableCell>
                        <TableCell align="right">Km</TableCell>
                        <TableCell>Nyomvonal</TableCell>
                        <TableCell>Megjegyzés</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fordTrips
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map((trip) => (
                          <TableRow key={trip.id} hover>
                            <TableCell>{trip.sortOrder + 1}</TableCell>
                            <TableCell>{trip.lineNumber}</TableCell>
                            <TableCell>{trip.tripNumber}</TableCell>
                            <TableCell>{trip.startTime}</TableCell>
                            <TableCell>{trip.startLocation}</TableCell>
                            <TableCell>{trip.endTime}</TableCell>
                            <TableCell>{trip.endLocation}</TableCell>
                            <TableCell align="right">{trip.km?.toFixed(2)}</TableCell>
                            <TableCell>{trip.nyomvon}</TableCell>
                            <TableCell>{trip.note || ''}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))}
          </Box>
        )}

        {/* Adatbázis tisztítás szekció - csak admin */}
        {isAdmin() && (
          <Paper sx={{ mt: 3, mb: 2 }}>
            <Box
              sx={{ 
                p: 2, 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' }
              }}
              onClick={() => setShowCleanup(!showCleanup)}
            >
              <DeleteSweepIcon color="error" sx={{ mr: 1 }} />
              <Typography variant="h6" color="error" sx={{ flexGrow: 1 }}>
                Adatbázis tisztítás
              </Typography>
              {showCleanup ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={showCleanup}>
              <Box sx={{ p: 3, pt: 0 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>Figyelem!</strong> Ez a funkció véglegesen törli a járatokat az adatbázisból. 
                  Biztonsági okokból csak 60 napnál régebbi járatokat lehet törölni.
                </Alert>

                {cleanupStats && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom>Járat statisztika:</Typography>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <Typography variant="body2">
                        <strong>Összes járat:</strong> {cleanupStats.totalTrips?.toLocaleString()}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Legrégebbi:</strong> {cleanupStats.oldestDate || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Legújabb:</strong> {cleanupStats.newestDate || '-'}
                      </Typography>
                      <Typography variant="body2" color="error">
                        <strong>Törölhető (60+ napos):</strong> {cleanupStats.deletableTrips?.toLocaleString()}
                      </Typography>
                    </Box>
                  </Paper>
                )}

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    label="Törlés eddig a napig (bezárólag)"
                    type="date"
                    value={cleanupDate}
                    onChange={(e) => {
                      setCleanupDate(e.target.value);
                      setCleanupError(null);
                      setCleanupSuccess(null);
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: maxCleanupDateStr }}
                    sx={{ minWidth: 250 }}
                    size="small"
                  />
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleCleanupCheck}
                    disabled={cleanupLoading || !cleanupDate}
                    startIcon={cleanupLoading ? <CircularProgress size={20} /> : <DeleteSweepIcon />}
                  >
                    {cleanupLoading ? 'Feldolgozás...' : 'Járatok törlése'}
                  </Button>
                </Box>

                {cleanupError && (
                  <Alert severity="error" sx={{ mt: 2 }} onClose={() => setCleanupError(null)}>
                    {cleanupError}
                  </Alert>
                )}

                {cleanupSuccess && (
                  <Alert severity="success" sx={{ mt: 2 }} onClose={() => setCleanupSuccess(null)}>
                    {cleanupSuccess}
                  </Alert>
                )}
              </Box>
            </Collapse>
          </Paper>
        )}

        {/* Megerősítő dialógus */}
        <Dialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
        >
          <DialogTitle sx={{ color: 'error.main' }}>
            ⚠️ Járatok törlése
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Biztosan törölni szeretnéd az összes járatot <strong>{cleanupDate}</strong>-ig (bezárólag)?
              <br /><br />
              Ez a művelet <strong>nem vonható vissza!</strong>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialogOpen(false)}>
              Mégsem
            </Button>
            <Button onClick={executeCleanup} color="error" variant="contained">
              Igen, törlés
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
}

export default Trips;
